import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../services/api';
import ArtistSelector from '../components/admin/ArtistSelector';
import { ArtistProfile } from '../types';
import { validateFile } from '../constants/fileUpload';
import './ArtistStorefront.css';

interface StorefrontCustomization {
  heroImageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  storefrontLayout?: string;
  showSalesBanner?: boolean;
  showFeaturedSection?: boolean;
  customMessage?: string;
}

const ArtistStorefront: React.FC = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'Admin';
  const [selectedArtistId, setSelectedArtistId] = useState<number | null>(null);
  const [artists, setArtists] = useState<ArtistProfile[]>([]);
  const [loadingArtists, setLoadingArtists] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingHero, setUploadingHero] = useState<boolean>(false);
  const [previewHeroUrl, setPreviewHeroUrl] = useState<string | null>(null);

  const [customization, setCustomization] = useState<StorefrontCustomization>({
    heroImageUrl: undefined,
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    accentColor: '#28a745',
    backgroundColor: '#f8f9fa',
    textColor: '#212529',
    storefrontLayout: 'default',
    showSalesBanner: true,
    showFeaturedSection: true,
    customMessage: ''
  });

  // Fetch artists list for admin
  const fetchArtists = useCallback(async (): Promise<void> => {
    if (!isAdmin) return;
    
    setLoadingArtists(true);
    try {
      const response = await api.get<ArtistProfile[]>('/admin/artists');
      setArtists(response.data);
      
      // Check if there's a specific artist in URL params
      const artistIdParam = searchParams.get('artistId');
      if (artistIdParam) {
        const artistId = parseInt(artistIdParam);
        const artist = response.data.find(a => a.userId === artistId);
        if (artist) {
          setSelectedArtistId(artistId);
        }
      } else {
        // Try to find David Brumagin by email
        const davidBrumagin = response.data.find(a => 
          a.email?.toLowerCase().includes('dbrumagin') || 
          a.email?.toLowerCase().includes('david')
        );
        if (davidBrumagin) {
          setSelectedArtistId(davidBrumagin.userId);
          setSearchParams({ artistId: davidBrumagin.userId.toString() });
        }
      }
    } catch (error) {
      console.error('Failed to fetch artists:', error);
      showError('Failed to load artists');
    } finally {
      setLoadingArtists(false);
    }
  }, [isAdmin, searchParams, setSearchParams, showError]);

  const fetchCustomization = useCallback(async (): Promise<void> => {
    const artistId = isAdmin ? selectedArtistId : user?.id;
    if (!artistId) {
      return;
    }

    // Ensure artistId is a number
    let numericArtistId: number;
    if (typeof artistId === 'number') {
      numericArtistId = artistId;
    } else {
      // Convert to string first, then clean and parse
      const artistIdStr = String(artistId);
      // Remove any non-numeric characters (like colons)
      const cleaned = artistIdStr.replace(/[^0-9]/g, '');
      numericArtistId = parseInt(cleaned, 10);
    }
    
    if (isNaN(numericArtistId) || numericArtistId <= 0) {
      showError('Invalid artist ID');
      return;
    }

    try {
      const response = await api.get<any>(`/artists/${numericArtistId}`);
      const profile = response.data;
      
      setCustomization({
        heroImageUrl: profile.heroImageUrl || undefined,
        primaryColor: profile.primaryColor || '#007bff',
        secondaryColor: profile.secondaryColor || '#6c757d',
        accentColor: profile.accentColor || '#28a745',
        backgroundColor: profile.backgroundColor || '#f8f9fa',
        textColor: profile.textColor || '#212529',
        storefrontLayout: profile.storefrontLayout || 'default',
        showSalesBanner: profile.showSalesBanner !== undefined ? profile.showSalesBanner : true,
        showFeaturedSection: profile.showFeaturedSection !== undefined ? profile.showFeaturedSection : true,
        customMessage: profile.customMessage || ''
      });

      if (profile.heroImageUrl) {
        const heroUrl = profile.heroImageUrl.startsWith('http')
          ? profile.heroImageUrl
          : `${API_BASE}${profile.heroImageUrl}`;
        setPreviewHeroUrl(heroUrl);
      }
    } catch (error: any) {
      console.error('Failed to fetch customization:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown'
        }
      });
      showError(error.response?.data?.message || 'Failed to load storefront settings');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedArtistId, user, showError]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!isAdmin && user.role !== 'Artist') {
      navigate('/profile');
      return;
    }

    if (isAdmin) {
      fetchArtists();
    }
  }, [user, navigate, isAdmin, fetchArtists]);

  useEffect(() => {
    if (isAdmin && selectedArtistId) {
      setLoading(true);
      fetchCustomization();
    } else if (!isAdmin && user?.id) {
      setLoading(true);
      fetchCustomization();
    }
  }, [isAdmin, selectedArtistId, user?.id, fetchCustomization]);

  const validateHexColor = (color: string): boolean => {
    // Must be #RRGGBB or #RGB format
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const handleColorChange = (field: keyof StorefrontCustomization, value: string): void => {
    // Validate hex color format
    if (value && !validateHexColor(value) && value.length > 0) {
      // Allow partial input while typing, but show warning if invalid when blurring
      setCustomization(prev => ({ ...prev, [field]: value }));
      return;
    }
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field: 'showSalesBanner' | 'showFeaturedSection'): void => {
    setCustomization(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleTextChange = (field: keyof StorefrontCustomization, value: string): void => {
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    const artistId = isAdmin ? selectedArtistId : user?.id;
    if (!file || !artistId) {
      showError('Please select an artist first');
      return;
    }

    // Validate file using centralized validation
    const validation = validateFile(file);
    if (!validation.isValid) {
      showError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear the input
      return;
    }

    // Ensure artistId is a number
    let numericArtistId: number;
    if (typeof artistId === 'number') {
      numericArtistId = artistId;
    } else {
      const artistIdStr = String(artistId);
      const cleaned = artistIdStr.replace(/[^0-9]/g, '');
      numericArtistId = parseInt(cleaned, 10);
    }
    
    if (isNaN(numericArtistId) || numericArtistId <= 0) {
      showError('Invalid artist ID');
      return;
    }

    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = `/artists/${numericArtistId}/storefront/hero-image`;
      const response = await api.post<{ heroImageUrl: string }>(
        url,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const heroUrl = response.data.heroImageUrl.startsWith('http')
        ? response.data.heroImageUrl
        : `${API_BASE}${response.data.heroImageUrl}`;
      
      setCustomization(prev => ({ ...prev, heroImageUrl: response.data.heroImageUrl }));
      setPreviewHeroUrl(heroUrl);
      success('Hero image uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload hero image:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown'
        }
      });
      showError(error.response?.data?.message || 'Failed to upload hero image');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleRemoveHeroImage = async (): Promise<void> => {
    const artistId = isAdmin ? selectedArtistId : user?.id;
    if (!artistId) return;

    // Ensure artistId is a number
    let numericArtistId: number;
    if (typeof artistId === 'number') {
      numericArtistId = artistId;
    } else {
      const artistIdStr = String(artistId);
      const cleaned = artistIdStr.replace(/[^0-9]/g, '');
      numericArtistId = parseInt(cleaned, 10);
    }
    
    if (isNaN(numericArtistId) || numericArtistId <= 0) {
      showError('Invalid artist ID');
      return;
    }

    try {
      // Update the customization to remove hero image URL (send empty string to remove)
      await api.put(`/artists/${numericArtistId}/storefront`, {
        heroImageUrl: ''
      });
      setCustomization(prev => ({ ...prev, heroImageUrl: undefined }));
      setPreviewHeroUrl(null);
      success('Hero image removed');
    } catch (error) {
      console.error('Failed to remove hero image:', error);
      showError('Failed to remove hero image');
    }
  };

  const handleSave = async (): Promise<void> => {
    const artistId = isAdmin ? selectedArtistId : user?.id;
    if (!artistId) {
      showError('Please select an artist first');
      return;
    }

    // Ensure artistId is a number
    let numericArtistId: number;
    if (typeof artistId === 'number') {
      numericArtistId = artistId;
    } else {
      const artistIdStr = String(artistId);
      const cleaned = artistIdStr.replace(/[^0-9]/g, '');
      numericArtistId = parseInt(cleaned, 10);
    }
    
    if (isNaN(numericArtistId) || numericArtistId <= 0) {
      showError('Invalid artist ID');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/artists/${numericArtistId}/storefront`, customization);
      success('Storefront customization saved successfully');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (): void => {
    const artistId = isAdmin ? selectedArtistId : user?.id;
    if (!artistId) {
      showError('Please select an artist first');
      return;
    }

    // Ensure artistId is a number
    let numericArtistId: number;
    if (typeof artistId === 'number') {
      numericArtistId = artistId;
    } else {
      const artistIdStr = String(artistId);
      const cleaned = artistIdStr.replace(/[^0-9]/g, '');
      numericArtistId = parseInt(cleaned, 10);
    }
    
    if (isNaN(numericArtistId) || numericArtistId <= 0) {
      showError('Invalid artist ID');
      return;
    }

    // Use window.location.origin to ensure the URL works correctly
    const previewUrl = `${window.location.origin}/artists/${numericArtistId}/shop`;
    window.open(previewUrl, '_blank');
  };

  const handleArtistSelect = (artistId: number | undefined): void => {
    const id = artistId ?? null;
    setSelectedArtistId(id);
    if (id) {
      setSearchParams({ artistId: id.toString() });
      setLoading(true);
    }
  };

  if (loading || (isAdmin && loadingArtists)) {
    return (
      <div className="artist-storefront-page">
        <div className="container">
          <div className="loading">Loading storefront settings...</div>
        </div>
      </div>
    );
  }

  if (isAdmin && !selectedArtistId) {
    return (
      <div className="artist-storefront-page">
        <div className="container">
          <div className="storefront-header">
            <h1>Manage Artist Storefront</h1>
            <p>Select an artist to customize their storefront.</p>
          </div>
          <div className="artist-selector-section">
            <ArtistSelector
              value={selectedArtistId || undefined}
              onChange={handleArtistSelect}
              placeholder="Search and select an artist..."
            />
            {artists.length === 0 && (
              <p className="no-artists">No artists found.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentArtist = isAdmin 
    ? artists.find(a => a.userId === selectedArtistId)
    : null;
  const artistName = currentArtist?.displayName || currentArtist?.email || 'Your';

  return (
    <div className="artist-storefront-page">
      <div className="container">
        {isAdmin && (
          <div className="admin-artist-selector">
            <label>Managing Storefront For:</label>
            <ArtistSelector
              value={selectedArtistId || undefined}
              onChange={handleArtistSelect}
              placeholder="Select artist..."
            />
          </div>
        )}
        <div className="storefront-header">
          <h1>Customize {isAdmin ? `${artistName}'s` : 'Your'} Storefront</h1>
          <p>Make {isAdmin ? 'this artist\'s' : 'your'} shop unique with custom colors, images, and layout options.</p>
        </div>

        <div className="storefront-form">
          {/* Hero Image Section */}
          <div className="form-section">
            <h2>Hero Image</h2>
            <p className="section-description">
              Upload a banner image that will be displayed at the top of your storefront.
            </p>
            <div className="hero-image-upload">
              {previewHeroUrl ? (
                <div className="hero-preview">
                  <img src={previewHeroUrl} alt="Hero preview" />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleRemoveHeroImage}
                  >
                    <i className="fas fa-trash"></i> Remove
                  </button>
                </div>
              ) : (
                <div className="hero-upload-placeholder">
                  <i className="fas fa-image"></i>
                  <p>No hero image uploaded</p>
                </div>
              )}
              <label className="file-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroImageUpload}
                  disabled={uploadingHero}
                  style={{ display: 'none' }}
                />
                <span className="btn btn-primary">
                  {uploadingHero ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload"></i> {previewHeroUrl ? 'Change' : 'Upload'} Hero Image
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>

          {/* Color Scheme Section */}
          <div className="form-section">
            <h2>Color Scheme</h2>
            <p className="section-description">
              Customize the colors used throughout your storefront.
            </p>
            <div className="color-grid">
              <div className="color-input-group">
                <label>Primary Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={customization.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={customization.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                    placeholder="#007bff"
                  />
                </div>
              </div>
              <div className="color-input-group">
                <label>Secondary Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={customization.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={customization.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                    placeholder="#6c757d"
                  />
                </div>
              </div>
              <div className="color-input-group">
                <label>Accent Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={customization.accentColor}
                    onChange={(e) => handleColorChange('accentColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={customization.accentColor}
                    onChange={(e) => handleColorChange('accentColor', e.target.value)}
                    placeholder="#28a745"
                  />
                </div>
              </div>
              <div className="color-input-group">
                <label>Background Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={customization.backgroundColor}
                    onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={customization.backgroundColor}
                    onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                    placeholder="#f8f9fa"
                  />
                </div>
              </div>
              <div className="color-input-group">
                <label>Text Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={customization.textColor}
                    onChange={(e) => handleColorChange('textColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={customization.textColor}
                    onChange={(e) => handleColorChange('textColor', e.target.value)}
                    placeholder="#212529"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Layout Section */}
          <div className="form-section">
            <h2>Layout</h2>
            <p className="section-description">
              Choose how your storefront is displayed.
            </p>
            <div className="layout-selector">
              <label>
                <input
                  type="radio"
                  name="layout"
                  value="default"
                  checked={customization.storefrontLayout === 'default'}
                  onChange={(e) => handleTextChange('storefrontLayout', e.target.value)}
                />
                <div className="layout-option">
                  <strong>Default</strong>
                  <span>Standard layout with hero and artworks grid</span>
                </div>
              </label>
              <label>
                <input
                  type="radio"
                  name="layout"
                  value="minimal"
                  checked={customization.storefrontLayout === 'minimal'}
                  onChange={(e) => handleTextChange('storefrontLayout', e.target.value)}
                />
                <div className="layout-option">
                  <strong>Minimal</strong>
                  <span>Clean, minimal design with focus on artwork</span>
                </div>
              </label>
              <label>
                <input
                  type="radio"
                  name="layout"
                  value="gallery"
                  checked={customization.storefrontLayout === 'gallery'}
                  onChange={(e) => handleTextChange('storefrontLayout', e.target.value)}
                />
                <div className="layout-option">
                  <strong>Gallery</strong>
                  <span>Gallery-style layout with large images</span>
                </div>
              </label>
              <label>
                <input
                  type="radio"
                  name="layout"
                  value="featured"
                  checked={customization.storefrontLayout === 'featured'}
                  onChange={(e) => handleTextChange('storefrontLayout', e.target.value)}
                />
                <div className="layout-option">
                  <strong>Featured</strong>
                  <span>Emphasize featured artworks at the top</span>
                </div>
              </label>
            </div>
          </div>

          {/* Custom Message Section */}
          <div className="form-section">
            <h2>Custom Message</h2>
            <p className="section-description">
              Add a custom welcome message that appears on your storefront.
            </p>
            <textarea
              className="custom-message-input"
              value={customization.customMessage}
              onChange={(e) => handleTextChange('customMessage', e.target.value)}
              placeholder="Welcome to my art store! Browse my collection of original artworks..."
              rows={4}
              maxLength={1000}
            />
            <div className="char-count">
              {customization.customMessage?.length || 0} / 1000 characters
            </div>
          </div>

          {/* Display Options Section */}
          <div className="form-section">
            <h2>Display Options</h2>
            <p className="section-description">
              Control what sections are shown on your storefront.
            </p>
            <div className="toggle-options">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={customization.showSalesBanner}
                  onChange={() => handleToggle('showSalesBanner')}
                />
                <div className="toggle-content">
                  <strong>Show Sales Banner</strong>
                  <span>Display active sales prominently at the top</span>
                </div>
              </label>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={customization.showFeaturedSection}
                  onChange={() => handleToggle('showFeaturedSection')}
                />
                <div className="toggle-content">
                  <strong>Show Featured Section</strong>
                  <span>Display featured artworks in a separate section</span>
                </div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePreview}
              disabled={!(isAdmin ? selectedArtistId : user?.id)}
            >
              <i className="fas fa-eye"></i> Preview Storefront
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistStorefront;
